import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function initHistoryTimelineSlider() {
  const slider = document.querySelector('.history__timeline-slider');
  if (!slider) return;

  const prevButton = document.querySelector('.history__timeline-nav-prev');
  const nextButton = document.querySelector('.history__timeline-nav-next');

  const updateNavigationButtons = (swiper) => {
    if (prevButton) {
      prevButton.disabled = swiper.isBeginning;
    }
    if (nextButton) {
      nextButton.disabled = swiper.isEnd;
    }
  };

  const updateSlideOpacity = (swiper) => {
    // Get the current active slide index
    const activeIndex = swiper.activeIndex;
    // Get the number of slides per view (handles responsive breakpoints)
    const slidesPerView = swiper.slidesPerViewDynamic ? swiper.slidesPerViewDynamic() : swiper.params.slidesPerView;
    // Get all slides
    const slides = swiper.slides;
    
    // Remove visible class from all slides first
    slides.forEach((slide) => {
      slide.classList.remove('swiper-slide-visible');
    });
    
    // Add visible class to slides that should be visible
    // Start from activeIndex and show slidesPerView number of slides
    for (let i = 0; i < slidesPerView; i++) {
      const slideIndex = activeIndex + i;
      if (slideIndex < slides.length) {
        slides[slideIndex].classList.add('swiper-slide-visible');
      }
    }
  };

  const swiper = new Swiper(slider, {
    modules: [Navigation],
    slidesPerView: 1,
    slidesPerGroup: 1, // Advance one slide at a time
    spaceBetween: 20,
    speed: 600,
    allowTouchMove: true,
    loop: false,
    watchSlidesVisibility: true,
    
    // Responsive breakpoints
    breakpoints: {
      // Tablet: 2 slide
      768: {
        slidesPerView: 2,
        slidesPerGroup: 1,
        spaceBetween: 20,
      },
      960: {
        slidesPerView: 3,
        slidesPerGroup: 1,
        spaceBetween: 20,
      },
      // Desktop: 4 slides visible, but advance one at a time
      1280: {
        slidesPerView: 4,
        slidesPerGroup: 1,
        spaceBetween: 30,
      },
    },

    // Navigation arrows
    navigation: {
      nextEl: '.history__timeline-nav-next',
      prevEl: '.history__timeline-nav-prev',
    },

    // Events
    on: {
      init: function () {
        updateNavigationButtons(this);
        updateSlideOpacity(this);
        // Initialize custom timeline animation after Swiper is ready
        initTimelineAnimation();
      },
      slideChange: function () {
        updateNavigationButtons(this);
        updateSlideOpacity(this);
      },
      resize: function () {
        updateNavigationButtons(this);
        updateSlideOpacity(this);
      },
      transitionEnd: function () {
        updateSlideOpacity(this);
      },
    },
  });

  return swiper;
}

/**
 * Custom animation for timeline slider
 * - Staggers each timeline item
 * - Scales icon-circle from 0 to 1
 * - Fades up content (year, title, description) together simultaneously
 * - Fades in pseudo-element horizontal lines during animation
 */
function initTimelineAnimation() {
  const timelineContainer = document.querySelector('.history__timeline');
  if (!timelineContainer) return;

  const slides = document.querySelectorAll('.history__timeline-slide');
  if (slides.length === 0) return;

  // Animation settings
  const staggerDelay = 0.15; // Delay between each slide
  const iconDuration = 0.6; // Duration for icon scale animation
  const contentDuration = 0.6; // Duration for content fade-up
  const initialDelay = 0.3; // Initial delay before first animation starts

  // Set initial states
  slides.forEach((slide) => {
    const iconCircle = slide.querySelector('.history__timeline-icon-circle');
    const timelineItem = slide.querySelector('.history__timeline-item');
    const year = slide.querySelector('.history__timeline-year');
    const title = slide.querySelector('.history__timeline-title');
    const description = slide.querySelector('.history__timeline-description');

    // Set icon circle initial state (scale 0)
    if (iconCircle) {
      gsap.set(iconCircle, {
        scale: 0,
        transformOrigin: 'center center',
        willChange: 'transform'
      });
    }

    // Set content initial state (fade-up)
    const contentElements = [year, title, description].filter(Boolean);
    if (contentElements.length > 0) {
      gsap.set(contentElements, {
        y: 30,
        opacity: 0,
        willChange: 'transform, opacity'
      });
    }
  });

  // Create main timeline
  const masterTimeline = gsap.timeline({
    scrollTrigger: {
      trigger: timelineContainer,
      start: 'top bottom-=200',
      toggleActions: 'play none none none',
      onComplete: () => {
        // Clean up will-change after animation
        slides.forEach((slide) => {
          const iconCircle = slide.querySelector('.history__timeline-icon-circle');
          const contentElements = [
            slide.querySelector('.history__timeline-year'),
            slide.querySelector('.history__timeline-title'),
            slide.querySelector('.history__timeline-description')
          ].filter(Boolean);

          if (iconCircle) {
            gsap.set(iconCircle, { willChange: 'auto' });
          }
          if (contentElements.length > 0) {
            gsap.set(contentElements, { willChange: 'auto' });
          }
        });
      }
    }
  });

  // Animate each slide with stagger
  slides.forEach((slide, index) => {
    const iconCircle = slide.querySelector('.history__timeline-icon-circle');
    const timelineItem = slide.querySelector('.history__timeline-item');
    const year = slide.querySelector('.history__timeline-year');
    const title = slide.querySelector('.history__timeline-title');
    const description = slide.querySelector('.history__timeline-description');

    const contentElements = [year, title, description].filter(Boolean);
    const slideDelay = initialDelay + (staggerDelay * index);

    // Create timeline for this slide
    const slideTimeline = gsap.timeline();

    // Animate icon circle (scale from 0 to 1)
    if (iconCircle) {
      slideTimeline.to(iconCircle, {
        scale: 1,
        duration: iconDuration,
        ease: 'back.out(1.2)'
      }, 0); // Start at position 0 in slide timeline
    }

    // Animate content elements together (fade-up simultaneously)
    if (contentElements.length > 0) {
      slideTimeline.to(contentElements, {
        y: 0,
        opacity: 1,
        duration: contentDuration,
        ease: 'power2.out'
      }, 0); // Start at position 0 in slide timeline (same time as icon)
    }

    // Add this slide's timeline to master timeline with stagger delay
    masterTimeline.add(slideTimeline, slideDelay);
  });

  // Create separate timeline for line clip-path reveal animation
  initLineRevealAnimation(timelineContainer, slides);
}

/**
 * Separate timeline for revealing the horizontal line pseudo-elements
 * Uses clip-path inset to reveal from center outwards
 */
function initLineRevealAnimation(timelineContainer, slides) {
  if (slides.length === 0) return;

  // Set initial state - lines completely hidden
  // ::before starts clipped on right (100%), ::after starts clipped on left (100%)
  slides.forEach((slide) => {
    gsap.set(slide, {
      '--line-clip-before': '100%', // ::before clipped on right (will reveal left to right)
      '--line-clip-after': '100%'  // ::after clipped on left (will reveal right to left)
    });
  });

  // Create separate reveal timeline
  const revealTimeline = gsap.timeline({
    scrollTrigger: {
      trigger: timelineContainer,
      start: 'top bottom-=200',
      toggleActions: 'play none none none'
    }
  });

  // Animate both pseudo-elements simultaneously
  // ::before reveals left to right (clip from right: 100% -> 0%)
  // ::after reveals right to left (clip from left: 100% -> 0%)
  revealTimeline.to(slides, {
    '--line-clip-before': '0%',
    '--line-clip-after': '0%',
    duration: 0.8,
    ease: 'power2.out'
  }, 0.3); // Start at 0.3s to align with main animation
}

